import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import ToggleSwitch from '@codegouvfr/react-dsfr/ToggleSwitch';
import clsx from 'clsx';
import type { ProgrammingSubPlanFieldConfig } from 'maestro-shared/schema/SpecificData/ProgrammingSubPlanFieldConfig';
import {
  type SpecificData,
  UnknownValue,
  UnknownValueLabel
} from 'maestro-shared/schema/SpecificData/SpecificData';
import { assertUnreachable } from 'maestro-shared/utils/typescript';
import { Fragment, useMemo } from 'react';
import { AppMultiSelect } from 'src/components/_app/AppMultiSelect/AppMultiSelect';
import AppSelect from 'src/components/_app/AppSelect/AppSelect';
import { selectOptionsFromList } from 'src/components/_app/AppSelect/AppSelectOption';
import AppTextInput from 'src/components/_app/AppTextInput/AppTextInput';
import type { ZodObject } from 'zod';
import AppRadioButtons from '../../../../components/_app/AppRadioButtons/AppRadioButtons';
import AppTextAreaInput from '../../../../components/_app/AppTextAreaInput/AppTextAreaInput';
import type { UseForm } from '../../../../hooks/useForm';
import type { SpecificDataFormInputProps } from './SpecificDataForm';

type Props<T extends ZodObject, U extends UseForm<T>> = {
  specificData: SpecificData;
  onChange: (specificData: SpecificData) => void;
  fieldConfig: ProgrammingSubPlanFieldConfig;
  inputProps: SpecificDataFormInputProps;
  inputForm: U;
  disabled?: boolean;
};

function MatrixSpecificDataFormInput<T extends ZodObject>(
  props: Props<T, UseForm<T>>
) {
  const {
    specificData,
    onChange,
    fieldConfig,
    inputProps,
    inputForm,
    disabled
  } = props;
  const { field, required } = fieldConfig;
  const inputKey = field.key;

  const label = field.label;

  const whenValid = useMemo(() => `${label} correctement renseigné.`, [label]);

  const sortedOptions = useMemo(
    () => [...field.options].sort((a, b) => a.order - b.order),
    [field.options]
  );

  const optionValues = useMemo(
    () => sortedOptions.map((o) => o.value),
    [sortedOptions]
  );

  const optionLabels = useMemo(
    () => Object.fromEntries(sortedOptions.map((o) => [o.value, o.label])),
    [sortedOptions]
  );

  const testId = useMemo(() => {
    const key = inputKey.toLowerCase();
    const suffix =
      field.inputType === 'select' || field.inputType === 'selectWithUnknown'
        ? 'select'
        : field.inputType === 'radio'
          ? 'radio'
          : 'input';
    return `${key}-${suffix}`;
  }, [inputKey, field.inputType]);

  return (
    <Fragment key={inputKey}>
      {(() => {
        switch (field.inputType) {
          case 'text':
            return (
              <div
                className={clsx(
                  cx('fr-col-12', 'fr-col-sm-6'),
                  inputProps.classes?.container
                )}
              >
                <AppTextInput
                  defaultValue={(specificData as any)[inputKey] ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...specificData,
                      [inputKey]: e.target.value
                    })
                  }
                  inputForm={inputForm}
                  inputKey="specificData"
                  inputPathFromKey={[inputKey]}
                  label={label}
                  hintText={field.hintText ?? undefined}
                  whenValid={whenValid}
                  required={required}
                  disabled={disabled}
                  data-testid={testId}
                />
              </div>
            );

          case 'date':
            return (
              <div
                className={clsx(
                  cx('fr-col-12', 'fr-col-sm-6'),
                  inputProps.classes?.container
                )}
              >
                <AppTextInput
                  type="date"
                  defaultValue={(specificData as any)[inputKey] ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...specificData,
                      [inputKey]: e.target.value || null
                    })
                  }
                  inputForm={inputForm}
                  inputKey="specificData"
                  inputPathFromKey={[inputKey]}
                  label={label}
                  hintText={field.hintText ?? undefined}
                  whenValid={whenValid}
                  required={required}
                  disabled={disabled}
                  data-testid={testId}
                />
              </div>
            );

          case 'number':
          case 'decimal':
            return (
              <div className={cx('fr-col-12', 'fr-col-sm-6')}>
                <AppTextInput
                  type="number"
                  defaultValue={(specificData as any)[inputKey] ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...specificData,
                      [inputKey]: e.target.value
                    })
                  }
                  inputForm={inputForm}
                  inputKey="specificData"
                  inputPathFromKey={[inputKey]}
                  label={label}
                  hintText={field.hintText ?? undefined}
                  whenValid={whenValid}
                  required={required}
                  disabled={disabled}
                  min={0}
                  step={field.inputType === 'decimal' ? 'any' : undefined}
                  data-testid={testId}
                />
              </div>
            );

          case 'textarea':
            return (
              <div
                className={clsx(
                  cx('fr-col-12', 'fr-col-sm-6'),
                  inputProps.classes?.container
                )}
              >
                <AppTextAreaInput
                  defaultValue={(specificData as any)[inputKey] ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...specificData,
                      [inputKey]: e.target.value
                    })
                  }
                  inputForm={inputForm}
                  inputKey="specificData"
                  inputPathFromKey={[inputKey]}
                  label={label}
                  hintText={field.hintText ?? undefined}
                  whenValid={whenValid}
                  required={required}
                  disabled={disabled}
                  rows={3}
                  data-testid={testId}
                />
              </div>
            );

          case 'select':
          case 'selectWithUnknown':
            return (
              <div
                className={clsx(
                  cx('fr-col-12', 'fr-col-sm-6'),
                  inputProps.classes?.container
                )}
              >
                <AppSelect
                  defaultValue={(specificData as any)[inputKey] ?? ''}
                  options={selectOptionsFromList(
                    field.inputType === 'selectWithUnknown'
                      ? [...optionValues, UnknownValue]
                      : optionValues,
                    {
                      labels:
                        field.inputType === 'selectWithUnknown'
                          ? {
                              ...optionLabels,
                              [UnknownValue]: UnknownValueLabel
                            }
                          : optionLabels,
                      withDefault: 'auto'
                    }
                  )}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if ((specificData as any)[inputKey] !== newValue) {
                      onChange({
                        ...specificData,
                        [inputKey]: newValue === '' ? null : newValue
                      });
                    }
                  }}
                  inputForm={inputForm}
                  inputKey="specificData"
                  inputPathFromKey={[inputKey]}
                  label={label}
                  whenValid={whenValid}
                  required={required}
                  disabled={disabled}
                  data-testid={testId}
                />
              </div>
            );

          case 'checkbox':
            return (
              <div
                className={clsx(
                  cx('fr-col-12', 'fr-col-sm-6', 'fr-mt-2w'),
                  inputProps.classes?.container
                )}
              >
                <ToggleSwitch
                  label={label}
                  checked={Boolean((specificData as any)[inputKey])}
                  onChange={(checked) =>
                    onChange({
                      ...specificData,
                      [inputKey]: checked
                    })
                  }
                  showCheckedHint={false}
                  disabled={disabled}
                  data-testid={testId}
                />
              </div>
            );

          case 'radio':
            return (
              <div
                className={clsx(
                  cx('fr-col-12', 'fr-col-sm-6'),
                  inputProps.classes?.container
                )}
              >
                <AppRadioButtons
                  legend={label}
                  options={
                    selectOptionsFromList(optionValues, {
                      labels: optionLabels,
                      withDefault: false
                    }).map(({ label, value }) => ({
                      key: `${testId}-option-${value}`,
                      label,
                      nativeInputProps: {
                        checked: (specificData as any)[inputKey] === value,
                        onChange: () =>
                          onChange({
                            ...specificData,
                            [inputKey]: value
                          })
                      }
                    })) ?? []
                  }
                  colSm={4}
                  inputForm={inputForm}
                  inputKey="specificData"
                  inputPathFromKey={[inputKey]}
                  required={required}
                  disabled={disabled}
                  data-testid={testId}
                />
              </div>
            );

          case 'multiselect':
            return (
              <div
                className={clsx(
                  cx('fr-col-12', 'fr-col-sm-6'),
                  inputProps.classes?.container
                )}
              >
                <AppMultiSelect
                  items={optionValues}
                  values={((specificData as any)[inputKey] as string[]) ?? []}
                  onChange={(values) =>
                    onChange({
                      ...specificData,
                      [inputKey]: values.length > 0 ? values : null
                    })
                  }
                  keysWithLabels={optionLabels}
                  defaultLabel="option sélectionnée"
                  withSort={false}
                  inputForm={inputForm}
                  inputKey="specificData"
                  inputPathFromKey={[inputKey]}
                  label={label}
                  hint={field.hintText ?? undefined}
                  whenValid={whenValid}
                  required={required}
                  disabled={disabled}
                  data-testid={testId}
                />
              </div>
            );

          default:
            return assertUnreachable(field.inputType);
        }
      })()}
    </Fragment>
  );
}

export default MatrixSpecificDataFormInput;
